import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Card, Spinner, Table } from "react-bootstrap";
import { getAvailableEngagements, getRegistrations } from "../api/api";

const money = (value) => `${Number(value || 0).toFixed(2)} DT`;

function statusBadge(status) {
  if (status === "submitted") return <Badge bg="warning" text="dark">En cours</Badge>;
  if (status === "processed") return <Badge bg="success">Traite</Badge>;
  if (status === "rejected") return <Badge bg="danger">Refuse</Badge>;
  if (status === "active") return <Badge bg="success">Active</Badge>;
  if (status === "en_attente") return <Badge bg="warning" text="dark">En attente</Badge>;
  if (status === "terminee") return <Badge bg="secondary">Terminee</Badge>;
  if (status === "annulee") return <Badge bg="danger">Annulee</Badge>;
  return <Badge bg="light" text="dark">-</Badge>;
}

export default function HistoriqueAdherent() {
  const [registrations, setRegistrations] = useState([]);
  const [engagements, setEngagements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        setLoading(true);
        setError("");
        const [regRes, engRes] = await Promise.all([
          getRegistrations(),
          getAvailableEngagements(),
        ]);

        if (!mounted) return;
        setRegistrations(Array.isArray(regRes.data) ? regRes.data : []);
        setEngagements(Array.isArray(engRes.data) ? engRes.data : []);
      } catch (err) {
        if (mounted) setError("Impossible de charger votre historique.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, []);

  const submittedEngagements = useMemo(
    () => engagements.filter((item) => item.my_submission_status),
    [engagements]
  );

  const decisionNotifications = useMemo(
    () =>
      submittedEngagements.filter((item) =>
        ["processed", "rejected"].includes(item.my_submission_status)
      ),
    [submittedEngagements]
  );

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
        <div className="mt-2">Chargement de votre historique...</div>
      </div>
    );
  }

  if (error) return <Alert variant="danger">{error}</Alert>;

  return (
    <div className="p-4">
      <div className="mb-4">
        <h3 className="mb-1">Mon historique</h3>
        <div className="text-muted">
          Suivi de vos demandes et inscriptions.
        </div>
      </div>

      {decisionNotifications.length > 0 && (
        <Alert variant="info">
          <strong>Notifications :</strong>{" "}
          {decisionNotifications.map((item) => (
            <span key={item.id} className="me-2">
              {item.title} est {item.my_submission_status === "processed" ? "traite" : "refuse"}.
            </span>
          ))}
        </Alert>
      )}

      <div className="d-grid gap-4">
        <Card>
          <Card.Header><strong>Historique des engagements</strong></Card.Header>
          <Card.Body>
            {submittedEngagements.length === 0 ? (
              <Alert variant="light" className="mb-0">Aucune demande d'engagement envoyee.</Alert>
            ) : (
              <Table hover responsive className="mb-0">
                <thead>
                  <tr>
                    <th>Engagement</th>
                    <th>Formulaire</th>
                    <th>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {submittedEngagements.map((item) => (
                    <tr key={item.id}>
                      <td>{item.title}</td>
                      <td>{item.form_template_name || "-"}</td>
                      <td>{statusBadge(item.my_submission_status)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>

        <Card>
          <Card.Header><strong>Historique des abonnements</strong></Card.Header>
          <Card.Body>
            {registrations.length === 0 ? (
              <Alert variant="light" className="mb-0">Aucune inscription trouvee.</Alert>
            ) : (
              <Table hover responsive className="mb-0">
                <thead>
                  <tr>
                    <th>Evenement</th>
                    <th>Montant</th>
                    <th>Echeances</th>
                    <th>Montant mensuel</th>
                    <th>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {registrations.map((item) => (
                    <tr key={item.id}>
                      <td>{item.event_titre || `Evenement #${item.event}`}</td>
                      <td>{money(item.montant_net)}</td>
                      <td>{item.tranche || 1} mois</td>
                      <td>{money(item.montant_mensuel)}</td>
                      <td>{statusBadge(item.statut)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      </div>
    </div>
  );
}
